/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useMemo, useRef } from 'react';

import { cssMap, jsx } from '@compiled/react';
import type { IntlShape } from 'react-intl';
import { IntlProvider } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { token } from '@atlaskit/tokens';

import type { AiSuggestionsPlugin } from '../../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../../pm-plugins/main';

import { SuggestionIcon } from './SuggestionIcon';

const suggestionIconScaleVar = '--suggestion-icon-scale';
const suggestionIconRotateVar = '--suggestion-icon-rotate';
const suggestionIconOpacityVar = '--suggestion-icon-opacity';

const styles = cssMap({
	container: {
		position: 'absolute',
		transform: 'translateX(100%)',
		pointerEvents: 'all',
	},
	button: {
		display: 'block',
		borderRadius: token('radius.full'),
		[suggestionIconScaleVar]: '1',
		[suggestionIconRotateVar]: '0deg',
		[suggestionIconOpacityVar]: '1',
		'&:hover': {
			// for some reason we need a non-variable property here otherwise there's a type error
			display: 'block',
			[suggestionIconScaleVar]: 'calc(4/3)',
			[suggestionIconRotateVar]: '90deg',
			[suggestionIconOpacityVar]: '0',
		},
	},
	svgContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		transition: 'transform 240ms ease-out',
		transformOrigin: 'center center',
		transform: `scale(var(${suggestionIconScaleVar})) rotate(var(${suggestionIconRotateVar}))`,
	},
	baseIconColorForeignObject: {
		height: '100%',
		width: '100%',
		position: 'absolute',
		top: 0,
		left: 0,
		transition: 'opacity 240ms ease-out',
	},
	iconColor: {
		backgroundColor: token('color.icon.subtle'),
		opacity: `var(${suggestionIconOpacityVar})`,
	},
	iconColorHover: {
		opacity: `calc(1 - var(${suggestionIconOpacityVar}))`,
		background:
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			'conic-gradient(from 90deg, rgba(252, 167, 0, 1) 0deg, rgba(252, 167, 0, 1) 90deg, rgba(106, 153, 34, 1) 90deg, rgba(106, 153, 34, 1) 180deg, rgba(24, 104, 219, 1) 180deg, rgba(24, 104, 219, 1) 270deg, rgba(175, 89, 224, 1) 270deg, rgba(175, 89, 224, 1) 360deg)',
	},
});

const AnimatedIcon = () => (
	<span css={styles.svgContainer}>
		<SuggestionIcon label="" size="small">
			<div css={[styles.baseIconColorForeignObject, styles.iconColor]} />
			<div css={[styles.baseIconColorForeignObject, styles.iconColorHover]} />
		</SuggestionIcon>
	</span>
);

export function SuggestionIconButton({
	leftAdjustment,
	localId,
	api,
	intl,
	topAdjustment,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	intl?: IntlShape;
	leftAdjustment?: string;
	/**
	 * Base localId of the block this button sits on. The only identity this
	 * component needs: the suggestions at that block — and whether one of them is
	 * selected — are read from plugin state below rather than passed in, so the
	 * widget decoration hosting this button can keep a stable key and never
	 * re-create the `--button-<localId>` anchor. See `calculateSuggestionsDecorations`.
	 */
	localId: string;
	// Used to visually adjust the position so it lines up with block controls
	topAdjustment?: number;
}): JSX.Element {
	// Derived primitives (rather than the state maps) so the hook's shallow
	// equality check can skip re-renders that don't affect this button.
	const { firstSuggestionId, isSelected, shortDescription, suggestionsCount } =
		useSharedPluginStateWithSelector(api, ['aiSuggestions'], (state) => {
			const { suggestionIdsByBaseLocalId, suggestionById, selectedSuggestion } =
				state?.aiSuggestionsState ?? {};
			const suggestionIds = suggestionIdsByBaseLocalId?.get(localId);
			const firstSuggestionId: string | undefined = suggestionIds?.values().next().value;

			return {
				firstSuggestionId,
				isSelected: Boolean(selectedSuggestion && suggestionIds?.has(selectedSuggestion)),
				shortDescription: firstSuggestionId
					? suggestionById?.get(firstSuggestionId)?.shortDescription
					: undefined,
				suggestionsCount: suggestionIds?.size ?? 0,
			};
		});

	const isSelectingSuggestionRef = useRef(false);

	const onSelectSuggestion = useCallback(() => {
		if (isSelectingSuggestionRef.current) {
			return;
		}

		isSelectingSuggestionRef.current = true;

		const selectSuggestion = async (): Promise<void> => {
			try {
				const canCloseComment =
					(await api?.annotation?.actions.requestCloseInlineComment?.()) ?? true;

				if (!canCloseComment || !firstSuggestionId) {
					return;
				}

				api?.aiSuggestions?.actions.selectSuggestion(firstSuggestionId, {
					interactionPoint: 'card',
				});
			} finally {
				isSelectingSuggestionRef.current = false;
			}
		};

		void selectSuggestion();
	}, [api, firstSuggestionId]);

	const setHoveredSuggestion = (id: string | undefined) => {
		api?.core?.actions.execute(({ tr }) => {
			tr.setMeta(aiSuggestionsPluginKey, { action: 'HOVER_SUGGESTION', id });
			return tr;
		});
	};

	const tooltip = useMemo(
		() => ({
			content:
				suggestionsCount > 1
					? intl?.formatMessage(aiSuggestionsMessages.iconButtonSuggestionsCountLabel, {
							count: suggestionsCount,
						}) ||
						(shortDescription ?? '')
					: (shortDescription ?? ''),
			position: 'top' as const,
		}),
		[intl, shortDescription, suggestionsCount],
	);

	return (
		<IntlProvider
			defaultLocale={intl?.defaultLocale || 'en-US'}
			locale={intl?.locale || 'en-US'}
			messages={intl?.messages}
			formats={intl?.formats}
		>
			<span
				css={styles.container}
				style={{
					anchorName: `--button-${localId}`,
					top: topAdjustment ?? 0,
					right: `calc(${token('space.negative.300')} + ${token('space.negative.300')} - ${
						leftAdjustment ?? '0px'
					})`,
				}}
			>
				{
					// We keep the anchorName span rendered so that the suggestion card can reference it
					!isSelected && firstSuggestionId && (
						<span
							css={styles.button}
							onMouseEnter={() => setHoveredSuggestion(firstSuggestionId)}
							onMouseLeave={() => setHoveredSuggestion(undefined)}
							onFocus={() => setHoveredSuggestion(firstSuggestionId)}
							onBlur={() => setHoveredSuggestion(undefined)}
						>
							<IconButton
								isTooltipDisabled={false}
								tooltip={tooltip}
								shape="circle"
								type="button"
								label={tooltip.content}
								onClick={onSelectSuggestion}
								appearance="subtle"
								spacing="compact"
								icon={AnimatedIcon}
							/>
						</span>
					)
				}
			</span>
		</IntlProvider>
	);
}
